import React, { useEffect, useState } from "react";
import api, { API, formatIDR } from "../lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Download, LineChart } from "lucide-react";

const FinanceReportPanel = () => {
  const [finPeriod, setFinPeriod] = useState("monthly");
  const [finStart, setFinStart] = useState("");
  const [finEnd, setFinEnd] = useState("");
  const [finance, setFinance] = useState(null);

  useEffect(() => {
    api.get("/reports/finance", { params: { period: finPeriod, start: finStart, end: finEnd } })
      .then((r) => setFinance(r.data));
  }, [finPeriod, finStart, finEnd]);

  return (
    <div className="bg-white border border-[#E4E4E7]" data-testid="finance-report">
      <div className="px-6 py-4 border-b border-[#E4E4E7] flex items-center gap-2">
        <LineChart size={14} />
        <div className="label-cap">Laporan Keuangan</div>
      </div>
      <div className="p-6">
        <div className="flex items-end gap-3 flex-wrap mb-6">
          <div>
            <label className="label-cap block mb-1">Periode</label>
            <select value={finPeriod} onChange={(e) => setFinPeriod(e.target.value)} className="px-3 py-2 border border-[#E4E4E7] text-sm bg-white" data-testid="finance-period-select">
              <option value="weekly">Mingguan</option>
              <option value="monthly">Bulanan</option>
              <option value="yearly">Tahunan</option>
            </select>
          </div>
          <div>
            <label className="label-cap block mb-1">Dari</label>
            <input type="date" value={finStart} onChange={(e) => setFinStart(e.target.value)} className="px-3 py-2 border border-[#E4E4E7] text-sm font-mono bg-white" data-testid="finance-start-input" />
          </div>
          <div>
            <label className="label-cap block mb-1">Sampai</label>
            <input type="date" value={finEnd} onChange={(e) => setFinEnd(e.target.value)} className="px-3 py-2 border border-[#E4E4E7] text-sm font-mono bg-white" data-testid="finance-end-input" />
          </div>
          <button
            onClick={() => {
              const token = localStorage.getItem("om_token");
              const params = new URLSearchParams({ period: finPeriod, start: finStart, end: finEnd, token: token || "" });
              window.open(`${API}/reports/finance.xlsx?${params.toString()}`, "_blank");
            }}
            data-testid="finance-xlsx-download"
            className="px-4 py-2 bg-[#059669] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#047857] flex items-center gap-2"
          >
            <Download size={14} /> Download Excel
          </button>
        </div>
        {finance && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E4E4E7] border border-[#E4E4E7] mb-6">
              <Stat label="Total Pemasukan (Lunas)" value={formatIDR(finance.totals.income)} mono />
              <Stat label="Total Pengeluaran" value={formatIDR(finance.totals.expense)} mono />
              <Stat label="Laba Bersih" value={<span className={finance.totals.net >= 0 ? "text-[#059669]" : "text-[#DC2626]"}>{formatIDR(finance.totals.net)}</span>} mono />
            </div>
            <div className="bg-white border border-[#E4E4E7] p-4 mb-6">
              <div className="label-cap mb-3">Grafik Pemasukan vs Pengeluaran ({finPeriod === "weekly" ? "Mingguan" : finPeriod === "monthly" ? "Bulanan" : "Tahunan"})</div>
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={finance.series}>
                    <CartesianGrid stroke="#E4E4E7" vertical={false} />
                    <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: "#52525B" }} />
                    <YAxis tick={{ fontSize: 10, fill: "#52525B" }} tickFormatter={(v) => v >= 1_000_000 ? `${(v/1_000_000).toFixed(1)}jt` : v >= 1000 ? `${Math.round(v/1000)}rb` : v} />
                    <Tooltip formatter={(v) => formatIDR(v)} contentStyle={{ borderRadius: 0, border: "1px solid #E4E4E7", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="income" name="Pemasukan" fill="#059669" />
                    <Bar dataKey="expense" name="Pengeluaran" fill="#DC2626" />
                    <Bar dataKey="net" name="Laba Bersih" fill="#002FA7" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border border-[#E4E4E7]">
                <div className="px-4 py-3 border-b border-[#E4E4E7] label-cap">Pengeluaran per Kategori</div>
                <table className="w-full">
                  <tbody>
                    {finance.by_category.length === 0 && <tr><td className="p-4 text-sm text-[#52525B]">Belum ada pengeluaran.</td></tr>}
                    {finance.by_category.map((c) => (
                      <tr key={c.category} className="border-b border-[#E4E4E7]">
                        <td className="px-4 py-2 text-sm font-bold">{c.category}</td>
                        <td className="px-4 py-2 text-sm font-mono text-right">{formatIDR(c.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-white border border-[#E4E4E7]" data-testid="finance-payment-breakdown">
                <div className="px-4 py-3 border-b border-[#E4E4E7] label-cap">Pemasukan per Metode Bayar</div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E4E4E7]">
                      <th className="text-left label-cap px-4 py-2">Metode</th>
                      <th className="text-right label-cap px-4 py-2">Order</th>
                      <th className="text-right label-cap px-4 py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(!finance.by_payment_method || finance.by_payment_method.length === 0) && <tr><td colSpan="3" className="p-4 text-sm text-[#52525B]">Belum ada pemasukan lunas.</td></tr>}
                    {(finance.by_payment_method || []).map((pm) => (
                      <tr key={pm.method} className="border-b border-[#E4E4E7]">
                        <td className="px-4 py-2 text-sm font-bold">{pm.method}</td>
                        <td className="px-4 py-2 text-sm font-mono text-right">{pm.count}</td>
                        <td className="px-4 py-2 text-sm font-mono text-right">{formatIDR(pm.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, mono }) => (
  <div className="bg-white p-6">
    <div className="label-cap mb-2">{label}</div>
    <div className={`font-heading font-black tracking-tight ${mono ? "text-3xl font-mono" : "text-xl"}`}>{value}</div>
  </div>
);

export default FinanceReportPanel;
